/**
 * Every control in the Levels panel as data: one entry per row, tagged with
 * its tab. The panel renders these; search, tab counts and the per-tab
 * Show all / Hide all all work off the same list, so a control added here
 * shows up everywhere at once.
 *
 * Wiring is unchanged from the old single-list menu: the same store actions
 * and the same tooltip texts. Only where a control sits has moved.
 */
import React from 'react';
import { useLevelsStore, isOrderFlowLevel, type LevelType } from '../../store/levelsStore';
import { useVolumeProfileStore } from '../../store/volumeProfileStore';
import {
  useGexStore, GEX_ELEMENTS, GEX_ELEMENT_LABELS, GEX_UNDERLYINGS, EMPTY_GEX_SLOT,
  DEFAULT_GEX_EXPIRY_WINDOW, DEFAULT_GEX_METHOD, DEFAULT_GEX_CONVENTIONS, expiryWindowLabel,
} from '../../store/gexStore';
import type { GexUnderlying, GexConventionKey, GexOption } from '../../store/gexStore';
import { useKeyLevelPrefsStore } from '../../store/profileLevelsStore';
import { KEY_LEVEL_COLORS, gexSourceLine } from '../../utils/keyLevels';
import { CONFLUENCE_CALIBRATION, confluenceLabel } from '../../utils/confluence';
import { useAbsorptionStore } from '../../store/absorptionStore';
import VestSpikeSettingsRow from '../VestSpikeSettingsRow';
import { WickRejectionSettingsRow } from '../WickRejectionSettings';
import { CmeSpikeSettingsRow, VestScaleRow } from '../VolumePaneSettingsRows';
import type { SessionType, VolumeProfileSettings } from '../../types/volumeProfile';
import type { LevelsTab } from '../../store/levelsPanelUiStore';
import { LEVEL_CONFIGS, LEVEL_TAB } from './levelConfigs';

interface RowBase {
  id: string;
  tab: LevelsTab;
  label: string;
  /** Second line + tooltip; also searched. */
  description?: string;
  /** Sub-heading inside the tab. */
  group?: string;
  /** Extra search terms (e.g. the labels of the controls under ⚙). */
  keywords?: string;
  testId?: string;
}

export interface SwitchRow extends RowBase {
  kind: 'switch';
  on: boolean;
  set: (on: boolean) => void;
  disabled?: boolean;
  /** False keeps the row out of the per-tab Show all / Hide all. */
  bulk?: boolean;
  /** Volume-profile field this switch writes, so a bulk change posts once. */
  vpKey?: keyof VolumeProfileSettings;
  dots?: React.ReactNode;
  /** Secondary controls behind the row's ⚙ disclosure. */
  settings?: React.ReactNode;
  settingsLabel?: string;
  /** Always shown under the row (notes). */
  after?: React.ReactNode;
}

/** A row whose control (select, link, info block) sits inline instead of a switch. */
export interface ControlRow extends RowBase {
  kind: 'control';
  control?: React.ReactNode;
  /** Full-width body under the label. */
  body?: React.ReactNode;
}

export type LevelRow = SwitchRow | ControlRow;

export interface LevelRowsModel {
  rows: LevelRow[];
  /** Read-only GEX status ("NDX live · SPX n/a"), shown on the Gamma tab. */
  gexStatus: React.ReactNode;
  /** levelsStore counts, for the action-bar badge and the global show/hide. */
  levelsEnabled: number;
  levelsTotal: number;
  setAllLevelsVisible: (visible: boolean) => void;
  /** Applies a per-tab show/hide; volume-profile fields go out in one save. */
  applyBulk: (rows: SwitchRow[], on: boolean) => void;
}

export const formatSessionType = (st: SessionType): string => {
  if (st.type === 'rth') return 'RTH';
  if (st.type === 'eth') return 'ETH';
  if (st.type === 'rolling') return `Rolling:${st.value}`;
  if (st.type === 'custom') return 'Custom';
  return 'Unknown';
};

export const parseSessionType = (value: string): SessionType => {
  if (value === 'RTH') return { type: 'rth' };
  if (value === 'ETH') return { type: 'eth' };
  if (value.startsWith('Rolling:')) {
    return { type: 'rolling', value: parseInt(value.split(':')[1]) };
  }
  return { type: 'rolling', value: 30 }; // default
};

// Two selects share a row with the index label; let them shrink rather than
// widen the panel (the full label is in the option list and the title).
const GEX_SETTING_SELECT_STYLE: React.CSSProperties = { flex: '1 1 0', minWidth: 0 };
/** Four convention selects per index wrap two to a line. */
const GEX_CONVENTION_SELECT_STYLE: React.CSSProperties = { flex: '1 1 40%', minWidth: 0 };

const gexUnderlyingLabel = (u: GexUnderlying) => (u === 'SPX' ? 'ES/MES' : 'NQ/MNQ');

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <span className="level-color-dot" style={{ background: color }} />
);

const ColorPick: React.FC<{ label: string; aria: string; value: string; onChange: (v: string) => void }> = ({
  label, aria, value, onChange,
}) => (
  <label className="levels-color-pick">
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} aria-label={aria} />
    {label}
  </label>
);

export function useLevelRows(): LevelRowsModel {
  // Price levels store
  const visibility = useLevelsStore((s) => s.visibility);
  const colors = useLevelsStore((s) => s.colors);
  const toggleLevel = useLevelsStore((s) => s.toggleLevel);
  const setLevelVisible = useLevelsStore((s) => s.setLevelVisible);
  const setAllLevelsVisible = useLevelsStore((s) => s.setAllLevelsVisible);
  const setLevelColor = useLevelsStore((s) => s.setLevelColor);

  // GEX. One menu for every chart: NDX levels draw on NQ/MNQ, SPX levels on ES/MES.
  const gexVisibility = useGexStore((g) => g.visibility);
  const toggleGexElement = useGexStore((g) => g.toggleElement);
  const gexByUnderlying = useGexStore((g) => g.byUnderlying);
  const gexSettings = useGexStore((g) => g.settings);
  const gexWindowOptions = useGexStore((g) => g.windowOptions);
  const gexMethodOptions = useGexStore((g) => g.methodOptions);
  const gexSettingsError = useGexStore((g) => g.settingsError);
  const gexSaving = useGexStore((g) => g.savingSettings);
  const setGexExpiryWindow = useGexStore((g) => g.setExpiryWindow);
  const setGexMethod = useGexStore((g) => g.setMethod);
  const setGexConvention = useGexStore((g) => g.setConvention);
  const gexConventionOptions = useGexStore((g) => g.conventionOptions);
  const gexSlots = GEX_UNDERLYINGS.map((u) => ({ u, slot: gexByUnderlying[u] ?? EMPTY_GEX_SLOT }));
  const gexAvailable = gexSlots.some(({ slot }) => slot.available);

  // Volume-profile key levels + confluence
  const kpSession = useKeyLevelPrefsStore((k) => k.sessionProfile);
  const kpWeekly = useKeyLevelPrefsStore((k) => k.weeklyProfile);
  const kpConfluence = useKeyLevelPrefsStore((k) => k.confluence);
  const kpTolerance = useKeyLevelPrefsStore((k) => k.confluenceTolerance);
  const toggleKeyPref = useKeyLevelPrefsStore((k) => k.toggle);
  const setConfluenceTolerance = useKeyLevelPrefsStore((k) => k.setConfluenceTolerance);

  // Absorption
  const absorptionAlerts = useAbsorptionStore((a) => a.alertsEnabled);
  const absorptionPredictive = useAbsorptionStore((a) => a.predictive);
  const setAbsorptionAlerts = useAbsorptionStore((a) => a.setAlertsEnabled);

  // Volume profile histogram (merges the partial with current and saves to the engine)
  const vpSettings = useVolumeProfileStore((s) => s.settings);
  const updateVp = (partial: Partial<VolumeProfileSettings>) => {
    const vp = useVolumeProfileStore.getState();
    vp.setSettings({ ...vp.settings, ...partial, updatedAt: Date.now() });
    vp.saveSettings();
  };

  const levelDots = (key: LevelType): React.ReactNode => {
    const c = colors[key];
    if (typeof c === 'string') {
      // Only the lines that are drawn in a single colour showed a dot before.
      return ['midnightOpen', 'mocOpen', 'donchian', 'ema9', 'ema21', 'ema200', 'vwap'].includes(key)
        ? <Dot color={c} /> : null;
    }
    if (c && typeof c === 'object' && 'high' in c) return <><Dot color={c.high} /><Dot color={c.low} /></>;
    return null;
  };

  const levelSettings = (key: LevelType): Pick<SwitchRow, 'settings' | 'settingsLabel' | 'keywords'> => {
    switch (key) {
      case 'onhOnl':
        return {
          settingsLabel: 'ONH/ONL colours',
          keywords: 'colour color',
          settings: (
            <div className="levels-gear-row" data-testid="onh-onl-colors">
              <ColorPick label="ONH" aria="ONH colour" value={colors.onhOnl.high} onChange={(v) => setLevelColor('onhOnl', v, true)} />
              <ColorPick label="ONL" aria="ONL colour" value={colors.onhOnl.low} onChange={(v) => setLevelColor('onhOnl', v, false)} />
            </div>
          ),
        };
      case 'vestVolume':
        return { settingsLabel: 'Vest volume scale', keywords: 'scale independent same units', settings: <VestScaleRow /> };
      case 'cmeVolSpikes':
        return {
          settingsLabel: 'CME spike rule',
          keywords: 'lookback threshold baseline auto time of day trailing multiples 2.4x',
          settings: <CmeSpikeSettingsRow />,
        };
      case 'vestSpikes':
        return { settingsLabel: 'Vest spike rule', keywords: 'lookback threshold avg', settings: <VestSpikeSettingsRow /> };
      case 'vestCat':
        // No settings since #599: the rule is the pane's own geometry.
        return { keywords: 'cat vest cme volume pane bar above taller sticks out' };
      case 'wickRejection':
        return { settingsLabel: 'Wick rejection rule', keywords: 'wick hook 🪝 reversal pin hammer emoji atr confirm level sweep', settings: <WickRejectionSettingsRow /> };
      default:
        return {};
    }
  };

  const rows: LevelRow[] = [];

  // Price, Indicators, Volume: the levelsStore toggles, in LEVEL_CONFIGS order.
  for (const c of LEVEL_CONFIGS) {
    rows.push({
      kind: 'switch',
      id: `level:${c.key}`,
      tab: LEVEL_TAB[c.key],
      label: c.label,
      description: c.description,
      on: !!visibility[c.key],
      set: (on) => { if (!!useLevelsStore.getState().visibility[c.key] !== on) toggleLevel(c.key); },
      dots: levelDots(c.key),
      testId: `level-toggle-${c.key}`,
      ...levelSettings(c.key),
    });
  }

  // Gamma
  for (const key of GEX_ELEMENTS) {
    rows.push({
      kind: 'switch',
      id: `gex:${key}`,
      tab: 'gamma',
      group: 'Levels',
      label: GEX_ELEMENT_LABELS[key].label,
      description: GEX_ELEMENT_LABELS[key].description,
      on: !!gexVisibility[key],
      set: (on) => { if (!!useGexStore.getState().visibility[key] !== on) toggleGexElement(key); },
      disabled: !gexAvailable,
      testId: `gex-toggle-${key}`,
    });
  }

  // Which expiries (and which method) the engine builds the walls, zero gamma
  // and IV bands from. Server-side, per index: changing it re-emits the levels
  // for every open chart. One row per index that has a slot.
  const gexIndexRows = gexSlots.filter(({ u }) => gexByUnderlying[u]);
  rows.push({
    kind: 'control',
    id: 'gex:settings',
    tab: 'gamma',
    group: 'Chain',
    label: 'Expiry window & method',
    description: 'Which expiries and which method the engine builds walls, zero gamma and IV bands from (per index, shared by every chart)',
    keywords: gexIndexRows.map(({ u }) => `${u} ${gexUnderlyingLabel(u)}`).join(' '),
    body: (
      <div className="levels-gex-settings">
        {gexIndexRows.length === 0 && <div className="levels-muted">Waiting for the engine's option chain.</div>}
        {gexIndexRows.map(({ u, slot }) => {
          const sel = slot.levels?.selection;
          const windowValue = gexSettings[u]?.expiryWindow ?? sel?.expiryWindow ?? DEFAULT_GEX_EXPIRY_WINDOW;
          const methodValue = gexSettings[u]?.method ?? sel?.method ?? DEFAULT_GEX_METHOD;
          const saving = !!gexSaving[u];
          // Keep an unknown server value selectable instead of silently showing another.
          const windowOpts = gexWindowOptions.some((o) => o.key === windowValue)
            ? gexWindowOptions : [...gexWindowOptions, { key: windowValue, label: windowValue }];
          const methodOpts = gexMethodOptions.some((o) => o.key === methodValue)
            ? gexMethodOptions : [...gexMethodOptions, { key: methodValue, label: methodValue }];
          return (
            <div key={u} className="levels-gex-index">
              <span className="levels-gex-index-label">{gexUnderlyingLabel(u)}</span>
              <select
                className="levels-select"
                style={GEX_SETTING_SELECT_STYLE}
                value={windowValue}
                onChange={(e) => setGexExpiryWindow(u, e.target.value)}
                disabled={saving}
                title={`${gexUnderlyingLabel(u)} expiry window for walls, zero gamma and IV bands`}
                aria-label={`${gexUnderlyingLabel(u)} expiry window`}
                data-testid={`gex-expiry-window-${u}`}
              >
                {windowOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <select
                className="levels-select"
                style={GEX_SETTING_SELECT_STYLE}
                value={methodValue}
                onChange={(e) => setGexMethod(u, e.target.value)}
                disabled={saving}
                title={`${gexUnderlyingLabel(u)} GEX method`}
                aria-label={`${gexUnderlyingLabel(u)} GEX method`}
                data-testid={`gex-method-${u}`}
              >
                {methodOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
          );
        })}
        {gexSettingsError && (
          <div className="levels-error" data-testid="gex-settings-error">{gexSettingsError}</div>
        )}
        {gexSlots.filter(({ slot }) => slot.available && slot.levels).map(({ u, slot }) => {
          const gexLevels = slot.levels!;
          return (
            <div key={u} className="levels-gex-source">
              <strong>{u}</strong> ({gexUnderlyingLabel(u)}) · {gexSourceLine(gexLevels)}
              <br />
              {/* The contract count is the honest measure of how much chain
                  this was built from; a thin chain makes thin walls. */}
              {gexLevels.contractsUsed} contracts · {gexLevels.expiriesUsed} expiries
              {gexLevels.selection ? (
                <> · {gexLevels.selection.expiryWindowLabel || expiryWindowLabel(gexLevels.selection.expiryWindow, gexWindowOptions)}
                  {(gexLevels.selection.expiries?.length ?? 0) > 0 && <>: {gexLevels.selection.expiries.join(', ')}</>}</>
              ) : gexLevels.nearExpiries && gexLevels.nearExpiries.length > 0 && (
                <> · near {gexLevels.nearExpiries.join(', ')}</>
              )}
              <br />
              net GEX {(gexLevels.netGex / 1e9).toFixed(2)}B
              {gexLevels.atmIv != null && <> · ATM IV {(gexLevels.atmIv * 100).toFixed(1)}%</>}
            </div>
          );
        })}
      </div>
    ),
  });
  // Per-level choices under the calibrated method: the put wall's book and
  // the IV band convention (anchor x IV source x horizon). Shared by every
  // chart like the window; meaningless under the legacy method, so disabled.
  const GEX_CONVENTION_FIELDS: { key: GexConventionKey; name: string; title: string }[] = [
    { key: 'putWallMethod', name: 'Put wall', title: 'Put wall method: OI only (default, closest to the reference) or OI + volume like the call wall' },
    { key: 'ivAnchor', name: 'IV anchor', title: 'IV bands are centred on this price' },
    { key: 'ivSource', name: 'IV source', title: 'Implied vol behind the IV bands' },
    { key: 'ivHorizon', name: 'IV horizon', title: 'Time the IV band move covers (trading time)' },
  ];
  rows.push({
    kind: 'control',
    id: 'gex:conventions',
    tab: 'gamma',
    group: 'Chain',
    label: 'Put wall & IV bands',
    description: 'Calibrated method only: put wall from OI only (default) or OI + volume; IV band anchor, IV source and horizon (per index)',
    keywords: `put wall iv bands anchor horizon source ${gexIndexRows.map(({ u }) => `${u} ${gexUnderlyingLabel(u)}`).join(' ')}`,
    body: (
      <div className="levels-gex-settings">
        {gexIndexRows.length === 0 && <div className="levels-muted">Waiting for the engine's option chain.</div>}
        {gexIndexRows.map(({ u, slot }) => {
          const sel = slot.levels?.selection;
          const methodValue = gexSettings[u]?.method ?? sel?.method ?? DEFAULT_GEX_METHOD;
          const legacy = methodValue === 'legacy';
          const saving = !!gexSaving[u];
          const valueOf = (k: GexConventionKey): string => {
            const fromSel = k === 'putWallMethod' ? (sel?.putWallMethod !== 'legacy' ? sel?.putWallMethod : undefined) : sel?.[k];
            return gexSettings[u]?.[k] ?? (fromSel || DEFAULT_GEX_CONVENTIONS[k]);
          };
          const optsFor = (k: GexConventionKey, v: string): GexOption[] =>
            gexConventionOptions[k].some((o) => o.key === v) ? gexConventionOptions[k] : [...gexConventionOptions[k], { key: v, label: v }];
          return (
            <div key={u} className="levels-gex-index levels-gex-conventions" data-testid={`gex-conventions-${u}`}>
              <span className="levels-gex-index-label">{gexUnderlyingLabel(u)}</span>
              {GEX_CONVENTION_FIELDS.map(({ key, name, title }) => {
                const v = valueOf(key);
                return (
                  <select
                    key={key}
                    className="levels-select"
                    style={GEX_CONVENTION_SELECT_STYLE}
                    value={v}
                    onChange={(e) => setGexConvention(u, key, e.target.value)}
                    disabled={saving || legacy}
                    title={legacy ? `${gexUnderlyingLabel(u)} ${name}: switch the method to Calibrated to use this` : `${gexUnderlyingLabel(u)} ${title}`}
                    aria-label={`${gexUnderlyingLabel(u)} ${name}`}
                    data-testid={`gex-${key}-${u}`}
                  >
                    {optsFor(key, v).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                  </select>
                );
              })}
            </div>
          );
        })}
      </div>
    ),
  });
  // Our levels against free public vendors over time (own page).
  rows.push({
    kind: 'control',
    id: 'gex:compare',
    tab: 'gamma',
    group: 'Chain',
    label: 'Compare with vendors',
    description: 'Compare our walls and zero gamma with free vendors, per preset',
    control: (
      <a
        href="/gex-compare"
        target="_blank"
        rel="noreferrer"
        className="levels-link"
        title="Compare our walls and zero gamma with free vendors, per preset"
        data-testid="gex-compare-link"
        data-nav=""
      >
        Open ↗
      </a>
    ),
  });

  // Profile: volume-profile key levels (independent of the histogram below:
  // the tags draw whether or not it is enabled), then the histogram settings.
  rows.push({
    kind: 'switch',
    id: 'key:sessionProfile',
    tab: 'profile',
    group: 'Key levels',
    label: 'POC / VAH / VAL',
    description: 'Session POC / VAH / VAL (session type and VA% from Volume Profile settings)',
    on: kpSession,
    set: (on) => { if (useKeyLevelPrefsStore.getState().sessionProfile !== on) toggleKeyPref('sessionProfile'); },
    dots: <><Dot color={KEY_LEVEL_COLORS.poc} /><Dot color={KEY_LEVEL_COLORS.vah} /></>,
    testId: 'key-session-profile',
  });
  rows.push({
    kind: 'switch',
    id: 'key:weeklyProfile',
    tab: 'profile',
    group: 'Key levels',
    label: 'Weekly POC / VAH / VAL',
    description: 'Weekly POC / VAH / VAL for the current CME week (Sun 5 PM - Fri 4 PM CT)',
    on: kpWeekly,
    set: (on) => { if (useKeyLevelPrefsStore.getState().weeklyProfile !== on) toggleKeyPref('weeklyProfile'); },
    dots: <><Dot color={KEY_LEVEL_COLORS.wpoc} /><Dot color={KEY_LEVEL_COLORS.wvah} /></>,
    testId: 'key-weekly-profile',
  });

  const vpSwitch = (
    key: 'enabled' | 'showDelta' | 'showCumulativeDelta' | 'showPoc' | 'showValueArea' | 'showVahVal',
    label: string,
    description: string,
    extra: Partial<SwitchRow> = {},
  ): SwitchRow => ({
    kind: 'switch',
    id: `vp:${key}`,
    tab: 'profile',
    group: 'Volume profile histogram',
    label,
    description,
    on: key === 'showVahVal' ? (vpSettings.showVahVal ?? true) : !!vpSettings[key],
    set: (on) => updateVp({ [key]: on }),
    vpKey: key,
    testId: `vp-${key}`,
    ...extra,
  });
  rows.push(vpSwitch('enabled', 'Volume profile', 'Draw the volume profile histogram (Enable)', { keywords: 'enable histogram' }));
  rows.push({
    kind: 'control',
    id: 'vp:sessionType',
    tab: 'profile',
    group: 'Volume profile histogram',
    label: 'Session',
    description: 'Range the profile is built over',
    keywords: 'RTH ETH rolling 30 min 60 min 2 hours',
    control: (
      <select
        className="levels-select"
        value={formatSessionType(vpSettings.sessionType)}
        onChange={(e) => updateVp({ sessionType: parseSessionType(e.target.value) })}
        aria-label="Volume profile session"
        data-testid="vp-session"
      >
        <option value="RTH">RTH</option>
        <option value="ETH">ETH</option>
        <option value="Rolling:30">30 min</option>
        <option value="Rolling:60">60 min</option>
        <option value="Rolling:120">2 hours</option>
      </select>
    ),
  });
  rows.push(vpSwitch('showDelta', 'Delta colors', 'Colour each profile row by buy/sell delta'));
  rows.push(vpSwitch('showCumulativeDelta', 'Cum. delta', 'Cumulative delta'));
  rows.push(vpSwitch('showPoc', 'POC', 'Point of control on the histogram'));
  rows.push(vpSwitch('showValueArea', 'Value Area', 'Shade the value area', {
    keywords: 'VA % percent',
    settingsLabel: 'Value area %',
    settings: (
      <div className="levels-gear-row levels-gear-slider">
        <span className="levels-gear-label">VA %</span>
        <input
          type="range"
          min="50"
          max="90"
          step="5"
          value={vpSettings.valueAreaPercent}
          onChange={(e) => updateVp({ valueAreaPercent: parseInt(e.target.value) })}
          className="levels-range"
          aria-label="Value area percent"
          data-testid="vp-va-percent"
        />
        <span className="levels-gear-value">{vpSettings.valueAreaPercent}%</span>
      </div>
    ),
  }));
  rows.push(vpSwitch('showVahVal', 'VAH/VAL lines', vpSettings.showValueArea
    ? 'Value area high / low lines'
    : 'Value area high / low lines (turn Value Area on first)', { disabled: !vpSettings.showValueArea }));

  // Markers: absorption-at-level candle markers + confluence zones.
  rows.push({
    kind: 'switch',
    id: 'level:absorption',
    tab: 'markers',
    group: 'Absorption',
    label: 'Absorption markers',
    description: 'Dot under (bullish) / above (bearish) a candle that traded into a key level on unusually large aggressive volume and closed back. Tag = level (ABS PW, ABS wVAL...).',
    on: !!visibility.absorption,
    set: (on) => setLevelVisible('absorption', on),
    dots: levelDots('absorption'),
    testId: 'absorption-toggle',
    keywords: 'ABS bull bear colour color',
    settingsLabel: 'Absorption colours',
    settings: (
      <div className="levels-gear-row" data-testid="absorption-colors">
        <ColorPick label="Bull" aria="Bullish absorption colour" value={colors.absorption.low} onChange={(v) => setLevelColor('absorption', v, false)} />
        <ColorPick label="Bear" aria="Bearish absorption colour" value={colors.absorption.high} onChange={(v) => setLevelColor('absorption', v, true)} />
      </div>
    ),
  });
  rows.push({
    kind: 'switch',
    id: 'absorption:alerts',
    tab: 'markers',
    group: 'Absorption',
    label: 'Push alerts to phone',
    description: 'Push each new absorption marker to the Signal Predictor app (arrives on its scanner-alerts channel, so that toggle must be on in the app). Shared by staging and production.',
    on: absorptionAlerts,
    set: (on) => setAbsorptionAlerts(on),
    // Never flipped by a tab-wide Show all: it pushes to the phone for both environments.
    bulk: false,
    testId: 'absorption-alerts',
    after: (
      <div className="levels-note" data-testid="absorption-note">
        {absorptionPredictive
          ? 'Held up out of sample. Hover a dot for its level\'s measured hold rate.'
          : 'Descriptive, not a prediction: on our tick history heavy volume at a level did not beat a plain close-back. Hover a dot for the measured rates.'}
      </div>
    ),
  });
  rows.push({
    kind: 'switch',
    id: 'key:confluence',
    tab: 'markers',
    group: 'Confluence',
    label: `Confluence (${confluenceLabel().split(' · ')[1]})`,
    description: `Mark where 2+ levels stack within ${kpTolerance} pts. Measured on ${CONFLUENCE_CALIBRATION.sessions} sessions: stacked ${CONFLUENCE_CALIBRATION.stacked.held}/${CONFLUENCE_CALIBRATION.stacked.total} held vs single ${CONFLUENCE_CALIBRATION.single.held}/${CONFLUENCE_CALIBRATION.single.total}.`,
    on: kpConfluence,
    set: (on) => { if (useKeyLevelPrefsStore.getState().confluence !== on) toggleKeyPref('confluence'); },
    dots: <Dot color="#ff5a1f" />,
    testId: 'confluence-toggle',
    keywords: 'within pts tolerance zone stacked',
    settingsLabel: 'Confluence tolerance',
    settings: (
      <div className="levels-gear-row">
        <label className="levels-gear-label" htmlFor="levels-confluence-tolerance">Within (pts)</label>
        <input
          id="levels-confluence-tolerance"
          type="number"
          min={1}
          max={50}
          step={0.25}
          value={kpTolerance}
          onChange={(e) => setConfluenceTolerance(parseFloat(e.target.value))}
          className="levels-select levels-number"
          data-testid="confluence-tolerance"
        />
      </div>
    ),
  });

  // Says plainly when the engine has nothing trustworthy, rather than leaving
  // toggles that silently draw nothing.
  const gexStatus = (
    <span className="levels-gex-status" data-testid="gex-status">
      {gexSlots.map(({ u, slot }, i) => (
        <span key={u} title={slot.available ? `${u} levels are live` : (slot.reason ?? 'waiting for chain')}>
          {i > 0 && ' · '}{u} {slot.available ? 'live' : 'n/a'}
        </span>
      ))}
    </span>
  );

  // Order-flow toggles belong to the TradeFinder panel, not these counts.
  const panelLevels = Object.entries(visibility).filter(([k]) => !isOrderFlowLevel(k));
  const levelsEnabled = panelLevels.filter(([, on]) => on).length;
  const levelsTotal = panelLevels.length;

  const applyBulk = (targets: SwitchRow[], on: boolean) => {
    const vpPatch: Partial<VolumeProfileSettings> = {};
    for (const r of targets) {
      if (r.bulk === false) continue;
      // VAH/VAL is only disabled because Value Area is off, which this same
      // patch may be turning on, so profile fields are not gated on it.
      if (r.vpKey) (vpPatch as Record<string, unknown>)[r.vpKey] = on;
      else if (!r.disabled) r.set(on);
    }
    if (Object.keys(vpPatch).length > 0) updateVp(vpPatch);
  };

  return { rows, gexStatus, levelsEnabled, levelsTotal, setAllLevelsVisible, applyBulk };
}
